import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { get_all_recordings } from "../../api";
import { useLogin } from "../../reducers";
import useRecordingControls from "../../utils/useRecordingControls";
import { useSettings } from "../../utils/SettingsContext";

type ActionItems = {
  /** Sessions held on this computer until the user approves their upload. */
  pendingReview: number;
  /** The user's own active sessions that have never been analyzed. */
  unanalyzed: number;
  /** Newly uploaded sessions awaiting manual analysis, excluding the initial backlog. */
  analysisReady: { id: number; count: number } | null;
  /** Reload the counts, after an action that may have settled some of them. */
  refresh: () => void;
};

const ActionItemsContext = createContext<ActionItems>({
  pendingReview: 0,
  unanalyzed: 0,
  analysisReady: null,
  refresh: () => {},
});

/** What the signed-in user still has to act on, as the avatar's badge and menu list it. */
export const useActionItems = () => useContext(ActionItemsContext);

/**
 * The desktop app reports the sessions awaiting review live. The unanalyzed ones are counted from
 * the session list, reloaded when a sync brings in a session, on each page change, and whenever a
 * page reports an action through `refresh`; a failed load keeps the last count.
 */
export function ActionItemsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useLogin();
  const { pathname } = useLocation();
  const { autoAnalyze } = useSettings();
  const { pendingReviewCount, recordingSyncCompletionId, recordingUploadCompletionId } =
    useRecordingControls();
  const [unanalyzed, setUnanalyzed] = useState(0);
  const [analysisReady, setAnalysisReady] = useState<ActionItems["analysisReady"]>(null);
  const analysisReadySequence = useRef(0);
  const knownUnanalyzedIds = useRef<Set<string> | null>(null);
  const handledUpload = useRef(recordingUploadCompletionId);
  const notifyAfterUpload = useRef(false);
  const generation = useRef(0);
  const refresh = useCallback(() => {
    const request = ++generation.current;
    get_all_recordings({ user_ids: [user.id], include_in_processes: true })
      .then(({ recordings }) => {
        if (request !== generation.current) return;
        const waiting = recordings.filter(
          item =>
            item.user_id === user.id &&
            item.include_in_processes &&
            item.status.status === "not_analyzed",
        );
        if (notifyAfterUpload.current) {
          const previous = knownUnanalyzedIds.current;
          const count = previous ? waiting.filter(item => !previous.has(item.id)).length : 0;
          if (!autoAnalyze && count > 0) {
            setAnalysisReady({ id: ++analysisReadySequence.current, count });
          }
          notifyAfterUpload.current = false;
        }
        knownUnanalyzedIds.current = new Set(waiting.map(item => item.id));
        setUnanalyzed(waiting.length);
      })
      .catch(() => {});
  }, [user.id, autoAnalyze]);
  useEffect(() => {
    knownUnanalyzedIds.current = null;
    notifyAfterUpload.current = false;
    setAnalysisReady(null);
  }, [user.id]);
  useEffect(() => {
    if (handledUpload.current !== recordingUploadCompletionId) {
      handledUpload.current = recordingUploadCompletionId;
      notifyAfterUpload.current = true;
    }
    refresh();
  }, [refresh, pathname, recordingSyncCompletionId, recordingUploadCompletionId]);
  return (
    <ActionItemsContext.Provider
      value={{ pendingReview: pendingReviewCount, unanalyzed, analysisReady, refresh }}
    >
      {children}
    </ActionItemsContext.Provider>
  );
}
